import React from 'react';
import * as Inovx from '@shared';
import FacilityActions from '../actions/FacilityActions';
import KnowledgeBookActions from '../actions/KnowledgeBookActions';
import FacilityStore from '../stores/FacilityStore';
import KnowledgeBookStore from '../stores/KnowledgeBookStore';
import ViewerStore from '../stores/ViewerStore';
import ComponentHelpers from '../helpers/ComponentHelpers';
import viewerActions from '@facility/actions/ViewerActions';
const UrlUtilities = Inovx.Shared.Common.UrlUtilities;

declare var InovxLoader: any;

interface FacilityExplorerSidebarProps {}

interface FacilityExplorerSidebarState {
    sidebarOpen?: boolean;
    assetSearchbarOpen?: boolean;
    selectedFacility?: any;
    expandedMenu?: string;
    isKnowledgeLibraryOpen?: boolean;
    isMediaGalleryOpen?: boolean;
    querySearch?: string;
    showMap?: boolean;
}

function getFormState(): FacilityExplorerSidebarState {
    return {
        selectedFacility: FacilityStore.getSelectedFacility(),
        sidebarOpen: FacilityStore.getSidebarOpen(),
        assetSearchbarOpen: FacilityStore.getAssetSearchbarOpen(),
        isKnowledgeLibraryOpen: FacilityStore.getKnowledgeLibraryOpen(),
        isMediaGalleryOpen: FacilityStore.getMediaGalleryOpen(),
        showMap: ViewerStore.isMiniMapOpen(),
    };
}

const FacilityExplorerSidebar: React.FC<FacilityExplorerSidebarProps> = () => {
    // Store-driven UI state snapshot
    const [storeState, setStoreState] = React.useState<FacilityExplorerSidebarState>({ ...getFormState() });

    // Non-render-impacting values
    const querySearchRef = React.useRef<string>('');
    const didMountRef = React.useRef<boolean>(false);

    // Memoized constants
    const baseUrl = React.useMemo(() => UrlUtilities.getBaseApplicationUrl(), []);
    const homeHref = React.useMemo(() => `/Home${storeState.sidebarOpen ? '?expand=true' : ''}`,[storeState.sidebarOpen]);
    const hasMediaGalleryAccess = React.useMemo(() => ComponentHelpers.hasFacilityExplorerPermission(), []);

    // Subscribe to FacilityStore updates
    React.useEffect(() => {
        const handler = () => setStoreState(getFormState());
        FacilityStore.addChangeListener(handler);
        ViewerStore.addChangeListener(handler);
        return () => {
            FacilityStore.removeChangeListener(handler);
            ViewerStore.removeChangeListener(handler);
        };
    }, []);

    // On mount: check URL for expand flag
    React.useEffect(() => {
        const currentUrl = window.location.href;
        const urlObject = new URL(currentUrl);
        const searchParams = new URLSearchParams(urlObject.search);
        const isExpand = searchParams.get('expand');
        if (isExpand && isExpand === 'true') {
            setTimeout(() => {
                FacilityActions.openSidebar();
            }, 0);
            querySearchRef.current = '?expand=true';
            // Toggle classes so layout matches expanded state immediately
            toggleMainContainerClass('view-expanded', 'view-collapsed');
        }
    }, []);

    React.useEffect(() => {
        if (!hasMediaGalleryAccess && storeState.isMediaGalleryOpen) {
            FacilityActions.closeMediaGallery();
        }
    }, [hasMediaGalleryAccess, storeState.isMediaGalleryOpen]);

    // Fire window resize when sidebarOpen changes (skip initial mount)
    // React.useEffect(() => {
    //     if (!didMountRef.current) {
    //         didMountRef.current = true;
    //         return;
    //     }
    //     const id = setTimeout(() => {
    //         window.dispatchEvent(new Event('resize'));
    //     }, 1);
    //     return () => clearTimeout(id);
    // }, [storeState.sidebarOpen]);

    const toggleMainContainerClass = (addClass: string = '', removeClass: string = '') => {
        const elements = document.getElementsByClassName('facility-app-container');
        for (let i = 0; i < elements.length; i++) {
            elements[i].classList.add(addClass);
            elements[i].classList.remove(removeClass);
        }
    };

    const toggleSidebar = () => {
        if (storeState.sidebarOpen) {
            toggleMainContainerClass('view-collapsed', 'view-expanded');
            FacilityActions.closeSidebar();
            querySearchRef.current = FacilityActions.updateUrlWithExpanded('false');
        } else {
            toggleMainContainerClass('view-expanded', 'view-collapsed');
            FacilityActions.openSidebar();
            querySearchRef.current = FacilityActions.updateUrlWithExpanded('true');
        }
    };

    const toggleAssetSearchbar = () => {
        if (storeState.assetSearchbarOpen) {
            FacilityActions.closeAssetSearchbar();
        } else {
            FacilityActions.openAssetSearchbar();
        }
    };
    const hasUnsavedKBData = (): boolean => {
        if (KnowledgeBookStore.getHasChanges()) {
            return true;
        } else if (KnowledgeBookStore.getKBModalVisible()) {
            KnowledgeBookActions.finishEdit();
        }
    }
    const hidePopup = () => {
        if (KnowledgeBookStore.getKVModalVisible()) {
            KnowledgeBookActions.finishKnowledgeViewEdit();
        }
        if (KnowledgeBookStore.getKBModalVisible()) {
            KnowledgeBookActions.finishEdit();
        }
    }
    const openFacilityMap = () => {
        if (hasUnsavedKBData()) return;
        hidePopup();
        FacilityActions.closeKnowledgeLibrary();
        FacilityActions.closeMediaGallery();
    };

    const open3DModel = async () => {
        if (hasUnsavedKBData()) return;
        hidePopup();
        const ontology = FacilityStore.getSelectedOntology();
        await FacilityActions.openLODViewer(ontology.ID, ["Point Cloud Data", "CAD Data", "Mesh Data"]);
    };

    const openKnowledgeLibrary = async () => {
        if (storeState.assetSearchbarOpen) {
            FacilityActions.closeAssetSearchbar();
        }
        await FacilityActions.openKnowledgeLibrary();
    };

    const openMediaGallery = () => {
        if (hasUnsavedKBData()) return;
        if (storeState.assetSearchbarOpen) {
            FacilityActions.closeAssetSearchbar();
        }
        hidePopup();
        FacilityActions.openMediaGallery();
    };

    const selectedMenu = storeState.isMediaGalleryOpen
        ? 'media-gallery'
        : storeState.isKnowledgeLibraryOpen
            ? 'knowledge-library'
            : 'map';
    const contentTypes = FacilityStore.getSelectedOntologyPartContentTypes() || [];
    var canOpen3D = contentTypes.some(t =>
        t === 'Assets' || t === 'CAD Data' || t === 'Point Cloud Data' || t === 'Mesh Data'
    );

    const toggleMiniMap = () => {
        viewerActions.setMiniMapOpen(!storeState.showMap);
    };
    return (
        <div id="sidebar" className={`sidebar ${storeState.sidebarOpen ? 'show' : ''}`}>
            <div className="d-flex f-dc j-csb h-100p">
                <nav className="nav">
                    <ul className="list-unstyled components">
                        <li>
                            <a className="d-flex j-csb" onClick={() => toggleSidebar()}>
                                <div className="icon">
                                    <img src={`${baseUrl}/images/menus/menu_icon.png`} />
                                </div>
                            </a>
                        </li>
                        <li>
                            <a href={homeHref} className="v-tooltip" aria-label={InovxLoader.t('facility:menu.home')}>
                                <div className="icon">
                                    <img src={`${baseUrl}/images/menus/home_icon.png`} />
                                </div>
                                <div className="nav_name">{InovxLoader.t('facility:menu.home')}</div>
                            </a>
                        </li>
                        <li className={selectedMenu === 'map' ? 'active' : ''}>
                            <a className="v-tooltip" aria-label={InovxLoader.t('facility:menu.facilityMap')} onClick={() => openFacilityMap()}>
                                <div className="icon">
                                    <img src={`${baseUrl}/images/menus/FacilityMap.png`} />
                                </div>
                                <div className="nav_name">{InovxLoader.t('facility:menu.facilityMap')}</div>
                            </a>
                        </li>
                        {canOpen3D && (
                            <li>
                                <a
                                    className="v-tooltip"
                                    aria-label={InovxLoader.t('facility:menu.facilityModel')}
                                    onClick={() => open3DModel()}
                                >
                                    <div className="icon">
                                        <img src={`${baseUrl}/images/menus/3d_model.png`} />
                                    </div>
                                    <div className="nav_name">{InovxLoader.t('facility:menu.facilityModel')}</div>
                                </a>
                            </li>
                        )}
                        <li className={selectedMenu === 'knowledge-library' ? 'active' : ''}>
                            <a className="v-tooltip" aria-label={InovxLoader.t('facility:menu.knowledgeLibrary')} onClick={() => openKnowledgeLibrary()}>
                                <div className="icon">
                                    <img src={`${baseUrl}/images/menus/knowledge_library.png`} />
                                </div>
                                <div className="nav_name">{InovxLoader.t('facility:menu.knowledgeLibrary')}</div>
                            </a>
                        </li>
                        <li className={selectedMenu === 'media-gallery' ? 'active' : ''}>
                            <a className="v-tooltip" aria-label={InovxLoader.t('facility:menu.mediaGallery')} onClick={() => openMediaGallery()}>
                                <div className="icon">
                                    <img src={`${baseUrl}/images/menus/mediaGallery.png`} />
                                </div>
                                <div className="nav_name">{InovxLoader.t('facility:menu.mediaGallery')}</div>
                            </a>
                        </li>
                        {(!storeState.isKnowledgeLibraryOpen && !storeState.isMediaGalleryOpen) && (<>
                            <li><hr style={{ width: '90%', margin: '8px auto' }} /></li>
                            <li className={storeState.assetSearchbarOpen ? 'active' : ''}>
                                <a onClick={() => toggleAssetSearchbar()} className="v-tooltip" aria-label={InovxLoader.t('facility:menu.searchAssets')}>
                                    <div className="icon">
                                        <img src={`${baseUrl}/images/menus/AssetRegistry.png`} />
                                    </div>
                                    <div className="nav_name">{InovxLoader.t('facility:menu.searchAssets')}</div>
                                </a>
                            </li>
                        </>)}
                    </ul>
                </nav>
                {selectedMenu === 'media-gallery' && <nav className="nav bottom-sidebar">
                    <ul className="list-unstyled components">
                        <li className={storeState.showMap ? 'active-menu' : ''}>
                            <a onClick={() => toggleMiniMap()} className="v-tooltip" aria-label={InovxLoader.t('facility:menu.miniMap')}>
                                <div className="icon">
                                    <img src={`${baseUrl}/images/menus/miniMap.png`} />
                                </div>
                                <div className="nav_name">{InovxLoader.t('facility:menu.miniMap')}</div>
                            </a>
                        </li>
                    </ul>
                </nav>}
            </div>
        </div>
    );
};

export default FacilityExplorerSidebar;
